import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import './AdminDashboard.css';
import './AdminForms.css';

const emptyForm = {
  code: '',
  title: '',
  category: '',
  type: '',
  status: 'coming_soon',
  price_text: '',
  duration_text: '',
  level_text: '',
  short_desc: '',
  image_url: '',
  sort_order: 0,
  is_active: true,
  imageFile: null,
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const token =
    typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const loadProducts = () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${API_BASE}/api/admin/products`, { headers: authHeaders })
      .then((res) => setProducts(res.data.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      code: p.code || '',
      title: p.title || '',
      category: p.category || '',
      type: p.type || '',
      status: p.status || 'coming_soon',
      price_text: p.price_text || '',
      duration_text: p.duration_text || '',
      level_text: p.level_text || '',
      short_desc: p.short_desc || '',
      image_url: p.image_url || '',
      sort_order: p.sort_order ?? 0,
      is_active: !!p.is_active,
      imageFile: null,
    });
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return alert('يرجى تسجيل الدخول مرة أخرى');
    if (!form.code.trim() || !form.title.trim())
      return alert('الكود والعنوان مطلوبان');

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('code', form.code.trim());
      fd.append('title', form.title.trim());
      fd.append('category', form.category.trim());
      fd.append('type', form.type.trim());
      fd.append('status', form.status);
      fd.append('price_text', form.price_text.trim());
      fd.append('duration_text', form.duration_text.trim());
      fd.append('level_text', form.level_text.trim());
      fd.append('short_desc', form.short_desc.trim());
      fd.append('image_url', form.image_url.trim());
      fd.append('sort_order', String(form.sort_order ?? 0));
      fd.append('is_active', form.is_active ? '1' : '0');
      if (form.imageFile) fd.append('image', form.imageFile);

      if (editingId) {
        await axios.put(`${API_BASE}/api/admin/products/${editingId}`, fd, {
          headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.post(`${API_BASE}/api/admin/products`, fd, {
          headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
        });
      }

      startCreate();
      loadProducts();
    } catch (err) {
      alert(err.response?.data?.error || 'فشل حفظ المنتج');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!token) return;
    if (!window.confirm(`حذف المنتج "${p.title}"؟`)) return;
    try {
      await axios.delete(`${API_BASE}/api/admin/products/${p.id}`, {
        headers: authHeaders,
      });
      if (editingId === p.id) startCreate();
      loadProducts();
    } catch (err) {
      alert(err.response?.data?.error || 'فشل الحذف');
    }
  };

  const absoluteImageUrl = useMemo(() => {
    if (!form.image_url) return '';
    if (form.image_url.startsWith('http')) return form.image_url;
    return `${API_BASE}${form.image_url}`;
  }, [form.image_url]);

  return (
    <div className="admin-dashboard" dir="rtl">
      <h1 className="admin-page-title">المنتجات</h1>

      <div className="admin-page-content">
        <div className="admin-page-main">
          <div className="admin-list-header">
            <h2>قائمة المنتجات</h2>
            <button type="button" className="btn-primary" onClick={startCreate}>
              منتج جديد
            </button>
          </div>

          {loading ? (
            <div className="admin-loading">جاري التحميل...</div>
          ) : products.length === 0 ? (
            <div className="admin-empty">لا توجد منتجات بعد.</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>الكود</th>
                    <th>العنوان</th>
                    <th>الحالة</th>
                    <th>السعر</th>
                    <th>مفعّل؟</th>
                    <th>ترتيب</th>
                    <th>التحكم</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id}>
                      <td>{p.code}</td>
                      <td>{p.title}</td>
                      <td>{p.status}</td>
                      <td>{p.price_text || '-'}</td>
                      <td>{p.is_active ? 'نعم' : 'لا'}</td>
                      <td>{p.sort_order ?? 0}</td>
                      <td>
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => startEdit(p)}
                        >
                          تعديل
                        </button>
                        <button
                          type="button"
                          className="btn-link danger"
                          onClick={() => handleDelete(p)}
                        >
                          حذف
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="admin-page-side">
          <h2>{editingId ? 'تعديل منتج' : 'إضافة منتج'}</h2>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>الكود (Unique)</label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => handleChange('code', e.target.value)}
                required
                placeholder="مثال: apa7-templates"
              />
            </div>

            <div className="form-group">
              <label>العنوان</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
              />
            </div>

            <div className="form-row inline-row">
              <div className="form-group">
                <label>التصنيف</label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="form-input short"
                  placeholder="course / ebook / template"
                />
              </div>
              <div className="form-group">
                <label>النوع</label>
                <input
                  type="text"
                  value={form.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className="form-input short"
                  placeholder="pdf / video / word"
                />
              </div>
            </div>

            <div className="form-row inline-row">
              <div className="form-group">
                <label>الحالة</label>
                <select
                  value={form.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="form-input short"
                >
                  <option value="available">available</option>
                  <option value="coming_soon">coming_soon</option>
                </select>
              </div>
              <div className="form-group">
                <label>مفعّل؟</label>
                <select
                  value={form.is_active ? '1' : '0'}
                  onChange={(e) =>
                    handleChange('is_active', e.target.value === '1')
                  }
                  className="form-input short"
                >
                  <option value="1">نعم</option>
                  <option value="0">لا</option>
                </select>
              </div>
              <div className="form-group">
                <label>ترتيب</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => handleChange('sort_order', e.target.value)}
                  className="form-input short"
                />
              </div>
            </div>

            <div className="form-row inline-row">
              <div className="form-group">
                <label>السعر (نص)</label>
                <input
                  type="text"
                  value={form.price_text}
                  onChange={(e) => handleChange('price_text', e.target.value)}
                  className="form-input short"
                  placeholder="مجاني / 45 ريال"
                />
              </div>
              <div className="form-group">
                <label>المدة</label>
                <input
                  type="text"
                  value={form.duration_text}
                  onChange={(e) => handleChange('duration_text', e.target.value)}
                  className="form-input short"
                  placeholder="ساعتين / ملف وورد"
                />
              </div>
              <div className="form-group">
                <label>المستوى</label>
                <input
                  type="text"
                  value={form.level_text}
                  onChange={(e) => handleChange('level_text', e.target.value)}
                  className="form-input short"
                  placeholder="مبتدئ / متوسط"
                />
              </div>
            </div>

            <div className="form-group">
              <label>وصف مختصر</label>
              <textarea
                value={form.short_desc}
                onChange={(e) => handleChange('short_desc', e.target.value)}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>رابط الصورة (اختياري)</label>
              <input
                type="url"
                value={form.image_url}
                onChange={(e) => handleChange('image_url', e.target.value)}
                placeholder="https://..."
              />
              {absoluteImageUrl ? (
                <div style={{ marginTop: 10 }}>
                  <img
                    src={absoluteImageUrl}
                    alt="preview"
                    style={{ width: '100%', borderRadius: 10 }}
                  />
                </div>
              ) : null}
            </div>

            <div className="form-group">
              <label>رفع صورة (اختياري)</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleChange('imageFile', e.target.files?.[0] || null)
                }
              />
            </div>

            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'جارٍ الحفظ...' : 'حفظ'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;

